<template>
	<CRow class="mt-3 mb-4">
		<CCol v-if="!isEmptyObject(item)">
			<div class="d-flex mb-3 justify-content-between">
				<div class="mb-0">
					<h6 data-test-id="title">
						Reward products
					</h6>
					<div
						class="typo-caption color-black-45"
						data-test-id="description"
					>
						Set one or more SKUs for this promotion
					</div>
				</div>
				<div class="btn-add-wrapper">
					<CButton
						type="button"
						class="btn btn-add"
						color="secondary"
						data-test-id="button-add"
						@click="$refs['modal-dapp-reward-sku'].open()"
					>
						{{ `${!isEmptyObject(item) ? 'Edit' : 'Add'} reward product` }}
					</CButton>
				</div>
			</div>
			<CRow>
				<CCol v-if="!item.sku" class="mb-3" md="12">
					<BaseNoItemBanner
						text="There is no freebie SKU added."
					/>
				</CCol>
				<CCol v-else md="12">
					<div class="sku-container">
						<CRow class="sku-header typo-label">
							<CCol md="6">
								<span class="label">SKUs</span>
							</CCol>
							<CCol md="2">
								<span class="label text-right">Stock qty.</span>
							</CCol>
							<CCol md="2">
								<span class="label">Reward qty.</span>
							</CCol>
							<CCol md="2">
								<span class="label">Used</span>
							</CCol>
						</CRow>

						<div
							class="d-flex flex-column sku-items-container"
						>
							<CRow
								class="sku-item"
							>
								<CCol md="6" class="p-0">
									<SKUListItem
										class="sku-detail"
										:thumbnail="item.thumbnail"
										:name="item.name"
										:sku="item.sku"
										:is-primary="item.isPrimary"
									/>
								</CCol>
								<CCol md="2" class="item-quantity text-right">
									{{ item.stockAvailable | numberFormat }}
								</CCol>
								<CCol md="2" class="item-quantity text-right">
									{{ item.rewardQty | numberFormat }}
								</CCol>
								<CCol md="2" class="item-quantity text-right">
									{{ (item.usedRewardStock || 0) | numberFormat }}
								</CCol>
							</CRow>
						</div>
					</div>
				</CCol>
				<CCol md="12">
					<div
						v-if="$v.item.sku.$dirty && $v.item.sku.$invalid"
						class="danger mb-2"
					>
						{{ $t('global.error.required') }}
					</div>
				</CCol>
			</CRow>

			<CRow class="mt-2">
				<CCol md="12">
					<PromotionDiscountDescriptionDappForm
						ref="descriptionDappForm"
						:description="item.stockOption.params"
					/>
				</CCol>
			</CRow>

			<!-- iTEC min price -->
			<CRow v-if="isITec">
				<CCol v-if="!isEmptyObject(item)" md="6">
					<label class="mb-0">
						Min Price*
					</label>
					<div class="typo-caption color-black-45">
						Based on iTEC Min Price
					</div>
					<BaseInputNumber
						:value="item.priceMin"
						:decimal-scale="2"
						disabled
						append-text="฿"
						text-align="left"
					/>
				</CCol>
			</CRow>

			<!-- Discount value -->
			<template v-else>
				<CRow class="mb-2">
					<CCol md="12">
						<h3
							class="typo-h5 position-relative"
							data-test-id="dapp-discount-value-title"
						>
							Discount value
						</h3>
					</CCol>
				</CRow>
				<CRow>
					<CCol md="12">
						<BaseDropDown
							v-model="selectedDiscountType"
							:options="discountTypeOptions"
							:searchable="false"
							:allow-empty="false"
							class="select-custom"
							label="label"
							track-by="value"
							label-drop-down="Type"
						/>
					</CCol>
				</CRow>
				<CRow>
					<CCol md="12">
						<PromotionDiscountValue
							:key="item.stockOption.discountType"
							ref="discountValueRef"
							:default-data="item.stockOption.tempParams"
							:discount-type="getDiscountTypeObject"
							@update:is-valid="handleValidChange($event)"
						/>
					</CCol>
				</CRow>
			</template>

			<CRow class="mt-3">
				<CCol md="6">
					<BaseInputNumber
						v-model="item.stockOption.params.rewardMaximumAmount"
						:decimal-scale="2"
						:is-valid="!$v.item.stockOption.params.rewardMaximumAmount.$error"
						:invalid-feedback="discountErrors"
						label="Total Maximum reward*"
						append-text="฿"
						text-align="left"
					/>
				</CCol>

				<CCol md="6" class="d-flex flex-column justify-content-end">
					<div>
						<span class="used-reward-maximum-amount">
							{{ (item.stockOption.params.usedRewardMaximumAmount || 0) | numberFormat }} Baht
						</span>
						<span>
							Total discount used
						</span>
					</div>

					<!-- dummy height when invalid -->
					<div
						v-if="$v.item.stockOption.params.rewardMaximumAmount.$error"
						class="dummy-height"
					></div>
				</CCol>
				<CCol md="6">
					<label class="label">Total Discounts on website</label>
				</CCol>
			</CRow>
		</CCol>

		<ModalSkuFreebie
			ref="modal-dapp-reward-sku"
			:products="getItemForModal"
			:reserve-skus="reserveSkus"
			title="Select reward SKU"
			reward-qty-header="Reward qty."
			@update:products="handleProductListChange"
		/>
	</CRow>
</template>

<script>

import { required } from 'vuelidate/lib/validators';

import ModalSkuFreebie from '@/components/ModalSkuFreebie.vue';
import SKUListItem from '@/components/SKUListItem.vue';

import PromotionDiscountValue from '@/components/PromotionDiscountValue.vue';
import PromotionDiscountDescriptionDappForm from '@/components/PromotionDiscountDescriptionDappForm.vue';

import { isEmptyObject } from '../assets/js/helpers';

import {
	DISCOUNT_TYPE_DAPP_KEYS,
	DISCOUNT_TYPE_DAPP_LABELS,
	DAPP_REWARD_TYPE,
} from '../enums/promotions';

export default {
	name: 'PromotionDappRewardDiscount',

	components: {
		ModalSkuFreebie,
		SKUListItem,
		PromotionDiscountValue,
		PromotionDiscountDescriptionDappForm,
	},

	props: {
		rewardType: {
			type: String,
			default: '',
		},
		items: {
			type: Array,
			default: () => [],
			// {
			// 	"stockOption": {
			// 		"discountType": "ITEM_FIXED_DISCOUNT",
			// 		"params": {
			// 			"shortDescriptionTH": "Test fixed amount",
			// 			"shortDescriptionEN": "Test fixed amount",
			// 			"descriptionTH": "<p>magSafe(TH)</p>",
			// 			"descriptionEN": "<p>magSafe(EN)</p>"
			// 			"discount": "100", // fixed and percentage
			// 			"priceMin": "฿1,590.00", // itec
			// 			"rewardMaximumAmount": "xxxxx", // maximum dicount for percentage
			// 			"usedRewardMaximumAmount": "1000.00", // total discount
			// 		}
			// 	}
			// }
		},
		reserveSkus: {
			type: Array,
			default: () => [],
		},
	},

	validations() {
		return {
			item: {
				sku: {
					required,
				},
				stockOption: {
					discountType: {
						required,
					},
					params: {
						rewardMaximumAmount: {
							required,
							greaterThanZero: (value) => {
								return value > 0;
							},
						},
					},
				},
			},
		};
	},

	computed: {
		item() {
			return this.items[0];
		},
		isITec() {
			return this.rewardType === DAPP_REWARD_TYPE.ITEC;
		},
		discountTypeOptions() {
			return [
				{
					value: DISCOUNT_TYPE_DAPP_KEYS.DAPP_PERCENTAGE_DISCOUNT,
					label: DISCOUNT_TYPE_DAPP_LABELS[DISCOUNT_TYPE_DAPP_KEYS.DAPP_PERCENTAGE_DISCOUNT],
				},
				{
					value: DISCOUNT_TYPE_DAPP_KEYS.DAPP_FIXED_AMOUNT_DISCOUNT,
					label: DISCOUNT_TYPE_DAPP_LABELS[DISCOUNT_TYPE_DAPP_KEYS.DAPP_FIXED_AMOUNT_DISCOUNT],
				},
			];
		},
		getDiscountTypeObject() {
			if (isEmptyObject(this.item)) {
				return null;
			}
			return this.discountTypeOptions.find((option) => option.value === this.item.stockOption.discountType);
		},
		getItemForModal() {
			if (!isEmptyObject(this.item) && this.item.sku) {
				return [{
					...this.item,
					valid: true,
				}];
			}
			return [];
		},
		discountErrors() {
			if (this.$v.item.stockOption && !this.$v.item.stockOption.params.rewardMaximumAmount.required) {
				return this.$t('global.error.required');
			}

			if (this.$v.item.stockOption && !this.$v.item.stockOption.params.rewardMaximumAmount.greaterThanZero) {
				return this.$t('global.error.moreThan', { number: '0' });
			}

			return null;
		},
		selectedDiscountType: {
			get() {
				return this.getDiscountTypeObject;
			},
			set(option) {
				this.$set(this.item.stockOption, 'discountType', option ? option.value : '');
			},
		},
	},

	created() {
		if (!this.items.length) {
			this.items.push({});
		}
		if (isEmptyObject(this.item.stockOption)) {
			this.$set(this.item, 'stockOption', {});
		}
		if (this.item.stockOption.discountType === undefined) {
			if (this.isITec) {
				this.$set(this.item.stockOption, 'discountType', DISCOUNT_TYPE_DAPP_KEYS.ITEM_ITEC_MIN_PRICE);
			} else {
				this.$set(this.item.stockOption, 'discountType', DISCOUNT_TYPE_DAPP_KEYS.DAPP_PERCENTAGE_DISCOUNT);
			}
		}
		// case Edit
		if (!isEmptyObject(this.item.stockOption.params)) {
			this.$set(this.item.stockOption, 'tempParams', {});
			this.$set(this.item.stockOption.tempParams, 'discount', this.item.stockOption.params.discount);
			this.$set(this.item.stockOption.tempParams, 'maximumDiscount', this.item.stockOption.params.maximumDiscount);
		} else {
			this.$set(this.item.stockOption, 'params', {});
		}
		if (this.item.stockOption.params.shortDescriptionTH === undefined) {
			this.$set(this.item.stockOption.params, 'shortDescriptionTH', '');
		}
		if (this.item.stockOption.params.shortDescriptionEN === undefined) {
			this.$set(this.item.stockOption.params, 'shortDescriptionEN', '');
		}
		if (this.item.stockOption.params.descriptionTH === undefined) {
			this.$set(this.item.stockOption.params, 'descriptionTH', '');
		}
		if (this.item.stockOption.params.descriptionEN === undefined) {
			this.$set(this.item.stockOption.params, 'descriptionEN', '');
		}
		if (this.item.stockOption.params.discount === undefined) {
			this.$set(this.item.stockOption.params, 'discount', 0);
		}
		if (this.item.stockOption.params.maximumDiscount === undefined) {
			this.$set(this.item.stockOption.params, 'maximumDiscount', 0);
		}
		if (this.item.stockOption.params.priceMin === undefined) {
			this.$set(this.item.stockOption.params, 'priceMin', 0);
		}
		if (this.item.stockOption.params.rewardMaximumAmount === undefined) {
			this.$set(this.item.stockOption.params, 'rewardMaximumAmount', 0);
		}
		if (this.item.stockOption.params.usedRewardMaximumAmount === undefined) {
			this.$set(this.item.stockOption.params, 'usedRewardMaximumAmount', 0);
		}
	},

	methods: {
		isEmptyObject,
		handleProductListChange(list) {
			const newItem = list[0];
			if (!newItem) {
				return;
			}
			const selectedProduct = {
				...newItem,
				stockOption: this.item.stockOption,
				valid: newItem.valid,
			};
			this.$emit('update:products', [selectedProduct]);
		},
		handleValidChange(value) {
			const { data: formData } = value;

			this.item.stockOption.params.discount = formData.discount;
			this.item.stockOption.params.maximumDiscount = formData.maximumDiscount;
		},
		handleDiscountValueTouch() {
			this.$v.$touch();
			const isDiscountValueInvalid = this.isITec
				? false
				: this.$refs.discountValueRef.handleTouch();
			const descriptionDappFormRef = this.$refs.descriptionDappForm.$v;
			descriptionDappFormRef.$touch();
			return isDiscountValueInvalid || this.$v.$invalid || descriptionDappFormRef.$invalid;
		},
	},
};
</script>

<style lang="scss" scoped>
.sku-container {
	padding: 12px 16px;
	background-color: #f4f4f4;
	border-radius: 6px;

	.sku {
		// .sku-header
		&-header {
			padding: 0 0 8px;
		}

		// .sku-items-container
		&-items-container {
			gap: 12px;
		}

		// .sku-item
		&-item {
			align-items: center;

			background-color: $color-white;
		}

		// .sku-detail
		&-detail::v-deep {
			margin-bottom: 0 !important;

			.thumbnail-54 {
				padding-left: 0 !important;
			}

			.product-detail {
				align-self: center;
			}
		}
	}
}

.sku-item {
	border: 1px solid #ddd;
	border-radius: 6px;

	padding: 12px;
	margin: 0;

	.qty {
		margin: auto;
	}
}

.thumbnail {
	width: rem(54);
	height: rem(54);
	object-fit: contain;

	margin-right: 8px;
}

.dummy-height {
	height: 20px;
}

.label {
	@include typo-caption;

	font-weight: $font-weight-bold;
	color: $color-gray-700;
}

.used-reward-maximum-amount {
	color: #e30000;
	font-weight: $font-weight-bold;
}
</style>
